import { useMemo } from "react";
import { type TaktischesZeichen } from "taktische-zeichen-react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "tactical-signs";

export type TaktischeZeichenVorlage = {
  readonly id: string;
  readonly label: string;
  readonly zeichen: TaktischesZeichen;
};

function useTacticalSignsSyncModule(): SyncModule<
  ReadonlyArray<TaktischeZeichenVorlage>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Tactical sign templates are a new-Datenportal-only resource — the
      // legacy portal has no equivalent endpoint, so we simply don't fetch
      // anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchTacticalSignsNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchTacticalSignsNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<TaktischeZeichenVorlage>> {
  const response = await fetch(`${baseUrl}/api/device/sync/tactical-signs`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.tacticalSigns ?? [])
    .filter((entry: any) => entry.grundzeichen || entry.symbol)
    .map(parseTacticalSignNew);
}

function parseTacticalSignNew(entry: any): TaktischeZeichenVorlage {
  return {
    id: entry.id,
    label: entry.name,
    zeichen: {
      grundzeichen: entry.grundzeichen || undefined,
      fachaufgabe: entry.fachaufgabe || undefined,
      organisation: entry.organisation || undefined,
      einheit: entry.einheit || undefined,
      verwaltungsstufe: entry.verwaltungsstufe || undefined,
      funktion: entry.funktion || undefined,
      symbol: entry.symbol || undefined,
      text: entry.text || undefined,
      typ: entry.typ || undefined,
      name: entry.name || undefined,
      organisationName: entry.nameOrganisation || undefined,
    } as TaktischesZeichen,
  };
}

export function useTacticalSignsSync() {
  const module = useTacticalSignsSyncModule();
  useSyncModuleRegistration(module);
}

export function useTaktischeZeichenVorlagen(): ReadonlyArray<TaktischeZeichenVorlage> {
  return useSyncData(syncModuleId) ?? [];
}
